import axios, { AxiosError, AxiosInstance } from "axios";

export const BACKEND_URL: string = process.env.REACT_APP_BACKEND_URL || "";
export const API: string = `${BACKEND_URL}/api`;

const api: AxiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// iter49 P3 #18: 401 response interceptor. Whenever a logged-in user's
// session expires mid-action (cookie cleared by another tab, JWT TTL hit,
// password rotated, etc.), the next API call gets a 401. Without this,
// the user sees an error toast and has to navigate to /login manually.
// With it, we redirect right away — UX matches every other modern SaaS.
//
// IMPORTANT exclusions:
//   • /auth/me — public pages call this to detect login state. 401 is the
//     normal "you're anon" signal; redirecting on it would force every
//     visitor of / to /login. So we ONLY redirect 401s from OTHER endpoints.
//   • the user is already on /login/register/forgot/reset/verify (where
//     401 = inline wrong-creds error, not a session-expired event).
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error?.response?.status;
    const reqUrl = String(error?.config?.url || "");
    const path = (typeof window !== "undefined" && window.location?.pathname) || "";
    const onAuthPage = path.startsWith("/login") || path.startsWith("/register") ||
                        path.startsWith("/forgot-password") || path.startsWith("/reset-password") ||
                        path.startsWith("/verify-email");
    // /auth/me is the canonical "am I logged in?" probe — 401 is expected
    // for anon visitors and MUST NOT cascade into a redirect loop.
    const isAuthMeProbe = reqUrl.includes("/auth/me");
    if (status === 401 && !onAuthPage && !isAuthMeProbe) {
      try {
        const next = encodeURIComponent(path + (window.location.search || ""));
        window.location.replace(`/login?next=${next}`);
      } catch (_) { /* SSR / restricted context */ }
    }
    return Promise.reject(error);
  },
);

export default api;

/** Coerce an axios error into a user-facing string. Handles every
 * envelope shape the FastAPI backend emits today: `{detail: "msg"}`,
 * `{detail: [{msg, ...}]}` (Pydantic 422), `{detail: {msg, ...}}`,
 * and bare network errors. */
export function formatApiError(err: unknown): string {
  // Best-effort discriminant — we don't import AxiosError narrowly because
  // call sites also pass raw Errors and undefined.
  const anyErr = err as { response?: { data?: { detail?: unknown } }; message?: string } | undefined;
  const detail = anyErr?.response?.data?.detail;
  if (detail == null) return anyErr?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof (e as { msg?: unknown }).msg === "string" ? (e as { msg: string }).msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof (detail as { msg?: unknown }).msg === "string") {
    return (detail as { msg: string }).msg;
  }
  return String(detail);
}
